import type { ValidatedAssistantProposal } from '../../logic/validateAssistantData'
import type { AssistantAction, AssistantRuleKey } from '../../types'

/** review画面で1提案と現在データをまとめた表示model。 */
export type AssistantProposalViewModel = {
  readonly proposal: ValidatedAssistantProposal
  readonly primaryAction: AssistantAction
  readonly targetActions: readonly AssistantAction[]
  readonly referenceActions: readonly AssistantAction[]
}

/** rule単位の仮想化表示model。 */
export type AssistantProposalGroupViewModel = {
  readonly ruleKey: AssistantRuleKey
  readonly title: string
  readonly priority: number
  readonly proposals: readonly AssistantProposalViewModel[]
  readonly targetActionCount: number
}

/** Action複合キーをMap用文字列へ変換する。 */
export const createAssistantViewActionKey = ({
  sourceId,
  actionOrder,
}: {
  readonly sourceId: string
  readonly actionOrder: number
}): string => JSON.stringify([sourceId, actionOrder])

/** raw pending countをsidebar badge文字列へ変換する。 */
export const formatAssistantPendingBadge = ({
  count,
}: {
  readonly count: number
}): string | null => {
  if (count <= 0) {
    return null
  }
  return count >= 100 ? '99+' : count.toString()
}

/** readonlyまたは処理中なら提案変更操作を無効化する。 */
export const isAssistantMutationDisabled = ({
  readonly,
  busy,
}: {
  readonly readonly: boolean
  readonly busy: boolean
}): boolean => readonly || busy

/** 個別操作中のroute遷移とbeforeunload blocker状態を返す。 */
export const createAssistantOperationBlockState = ({
  operation,
  bulkRunning = false,
  currentPathname,
  nextPathname,
  currentPortfolioId,
  nextPortfolioId,
}: {
  readonly operation: {
    readonly type: 'approve' | 'reject'
    readonly proposalId: string
  } | null
  readonly bulkRunning?: boolean
  readonly currentPathname: string
  readonly nextPathname: string
  readonly currentPortfolioId: string | undefined
  readonly nextPortfolioId: string | undefined
}): {
  readonly blockRoute: boolean
  readonly enableBeforeUnload: boolean
} => ({
  blockRoute:
    (operation !== null || bulkRunning) &&
    (currentPathname !== nextPathname ||
      currentPortfolioId !== nextPortfolioId),
  enableBeforeUnload: operation !== null || bulkRunning,
})

/** 変更Action集合に現在開いているAction Sheetのidentityが含まれるか返す。 */
export const includesAssistantActionIdentity = ({
  changedActions,
  action,
}: {
  readonly changedActions: readonly {
    readonly sourceId: string
    readonly order: number
  }[]
  readonly action:
    | {
        readonly source: string
        readonly order: number
      }
    | undefined
}): boolean =>
  action !== undefined &&
  changedActions.some(
    (changedAction) =>
      changedAction.sourceId === action.source &&
      changedAction.order === action.order,
  )

/** mutation結果がproposal cacheへ反映可能なDB確定結果か返す。 */
export const isAssistantProposalMutationCommitted = ({
  status,
}: {
  readonly status:
    | 'approved'
    | 'content-conflict'
    | 'missing'
    | 'rejected'
    | 'technical-error'
}): boolean => status !== 'technical-error'

/** 個別操作と一括操作の確認dialog要否を返す。 */
export const requiresAssistantConfirmation = ({
  scope,
}: {
  readonly scope: 'individual' | 'bulk'
}): boolean => scope === 'bulk'

/** source IDを秘密値の全文露出を避けた識別表示へ変換する。 */
export const formatAssistantSourceIdentifier = ({
  sourceId,
}: {
  readonly sourceId: string
}): string => {
  const segments = sourceId.split('_')
  const identifier = segments[segments.length - 1] ?? sourceId
  return identifier.length <= 18
    ? identifier
    : `${identifier.slice(0, 10)}…${identifier.slice(-6)}`
}

/** Assetの表示名を現在データから生成する。 */
export const createAssistantAssetTitle = ({
  action,
}: {
  readonly action: AssistantAction
}): string =>
  action.transfers
    .map((transfer) => {
      if (transfer.asset.type === 'crypto') {
        return transfer.asset.symbol ?? transfer.asset.name ?? transfer.asset.id
      }
      if (transfer.asset.type === 'nft') {
        return transfer.asset.name ?? transfer.asset.id
      }
      if (transfer.asset.type === 'fiat') {
        return transfer.asset.id.toUpperCase()
      }
      return '不明なAsset'
    })
    .join(' / ')

/** 1 rule pageのproposalと現在Actionを表示modelへ変換する。 */
export const createAssistantProposalGroup = ({
  ruleKey,
  title,
  priority,
  proposals,
  actions,
}: {
  readonly ruleKey: AssistantRuleKey
  readonly title: string
  readonly priority: number
  readonly proposals: readonly ValidatedAssistantProposal[]
  readonly actions: readonly AssistantAction[]
}): AssistantProposalGroupViewModel => {
  const actionByKey = new Map(
    actions.map((action) => [
      createAssistantViewActionKey({
        sourceId: action.sourceId,
        actionOrder: action.order,
      }),
      action,
    ]),
  )
  const items: AssistantProposalViewModel[] = []
  for (const proposal of proposals) {
    if (proposal.draft.ruleKey !== ruleKey) {
      continue
    }
    const targetActions: AssistantAction[] = []
    const referenceActions: AssistantAction[] = []
    for (const relation of proposal.draft.relations) {
      const action = actionByKey.get(
        createAssistantViewActionKey({
          sourceId: relation.sourceId,
          actionOrder: relation.actionOrder,
        }),
      )
      if (action === undefined) {
        continue
      }
      if (relation.role === 'target') {
        targetActions.push(action)
      } else {
        referenceActions.push(action)
      }
    }
    const primaryAction = actionByKey.get(
      createAssistantViewActionKey({
        sourceId: proposal.draft.primaryAction.sourceId,
        actionOrder: proposal.draft.primaryAction.actionOrder,
      }),
    )
    if (primaryAction === undefined || targetActions.length === 0) {
      continue
    }
    items.push({
      proposal,
      primaryAction,
      targetActions,
      referenceActions,
    })
  }
  const sortedItems = items.toSorted(
    (left, right) =>
      left.primaryAction.timestamp - right.primaryAction.timestamp ||
      left.primaryAction.id.localeCompare(right.primaryAction.id) ||
      left.proposal.id.localeCompare(right.proposal.id),
  )
  return {
    ruleKey,
    title,
    priority,
    proposals: sortedItems,
    targetActionCount: new Set(
      sortedItems.flatMap((item) =>
        item.targetActions.map((action) =>
          createAssistantViewActionKey({
            sourceId: action.sourceId,
            actionOrder: action.order,
          }),
        ),
      ),
    ).size,
  }
}

/** 固定高さvirtual listが描画するindex範囲をテスト可能な形で計算する。 */
export const createAssistantVirtualRange = ({
  count,
  scrollOffset,
  viewportHeight,
  itemHeight,
  overscan,
}: {
  readonly count: number
  readonly scrollOffset: number
  readonly viewportHeight: number
  readonly itemHeight: number
  readonly overscan: number
}): readonly number[] => {
  if (count === 0 || viewportHeight <= 0 || itemHeight <= 0) {
    return []
  }
  const first = Math.max(0, Math.floor(scrollOffset / itemHeight) - overscan)
  const last = Math.min(
    count - 1,
    Math.ceil((scrollOffset + viewportHeight) / itemHeight) + overscan,
  )
  return Array.from({ length: last - first + 1 }, (_, index) => first + index)
}
